import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { Cargando, ErrorCarga } from '@/components/Estados';
import { Modal } from '@/components/Modal';
import { nombreEspecialidad, useEspecialidades } from '@/features/preguntas/queries';
import { fechaLarga } from '@/lib/formato';

import { ElegirLicencias } from './ElegirLicencias';
import {
  useAsignar,
  useElemento,
  useResumenBiblioteca,
  type ElementoBody,
  type ElementoDetalle,
} from './queries';

function Formulario({ actual, onListo }: { actual: ElementoDetalle; onListo: () => void }) {
  const qc = useQueryClient();
  const editable = useCan('contenido', 'editar');
  const esp = useEspecialidades();
  const resumen = useResumenBiblioteca();
  const asignar = useAsignar();
  const [f, setF] = useState({
    name: actual.name,
    description: actual.description ?? '',
    specialtyId: actual.specialtyId,
    isEnglish: actual.isEnglish,
    tags: actual.tags.join(', '),
  });
  const poner = (c: Partial<typeof f>) => setF((v) => ({ ...v, ...c }));
  const guardar = useMutation({
    mutationFn: () => {
      const body: ElementoBody = {
        name: f.name,
        description: f.description.trim() || null,
        specialtyId: f.specialtyId,
        isEnglish: f.isEnglish,
        tags: f.tags.split(',').map((t) => t.trim()),
      };
      return unwrap(
        api.PATCH('/api/v1/admin/library-items/{id}', {
          params: { path: { id: String(actual.id) } },
          body,
        }),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contenido'] });
      toast.success('Elemento guardado.');
      onListo();
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  const asignadas = new Set(actual.memberships.map((m) => m.id));

  return (
    <div className="space-y-6">
      <p className="text-sm text-texto-3">
        {actual.typeLabel} · #{actual.id}
        {actual.code && ` · ${actual.code}`}
        {actual.updatedAt && ` · actualizado ${fechaLarga(actual.updatedAt)}`}
      </p>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          guardar.mutate();
        }}
      >
        <fieldset disabled={!editable} className="space-y-4">
          <Campo etiqueta="Nombre" ayuda="También cambia en el registro del tipo (clase, video…).">
            <input
              className="input-vidrio"
              value={f.name}
              required
              minLength={2}
              maxLength={250}
              onChange={(e) => poner({ name: e.target.value })}
            />
          </Campo>
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo etiqueta="Especialidad">
              <select
                className="input-vidrio"
                value={f.specialtyId ?? ''}
                onChange={(e) =>
                  poner({ specialtyId: e.target.value ? Number(e.target.value) : null })
                }
              >
                <option value="">Sin especialidad</option>
                {esp.data?.map((e) => (
                  <option key={e.id} value={e.id}>
                    {nombreEspecialidad(e)}
                  </option>
                ))}
              </select>
            </Campo>
            <Campo etiqueta="Idioma">
              <select
                className="input-vidrio"
                value={f.isEnglish ? 'en' : 'es'}
                onChange={(e) => poner({ isEnglish: e.target.value === 'en' })}
              >
                <option value="es">Español</option>
                <option value="en">Inglés</option>
              </select>
            </Campo>
          </div>
          <Campo etiqueta="Etiquetas" ayuda="Separadas por coma. Sirven para buscar.">
            <input
              className="input-vidrio"
              value={f.tags}
              onChange={(e) => poner({ tags: e.target.value })}
            />
          </Campo>
          <Campo etiqueta="Descripción">
            <textarea
              className="input-vidrio min-h-20"
              value={f.description}
              maxLength={250}
              onChange={(e) => poner({ description: e.target.value })}
            />
          </Campo>
        </fieldset>
        {editable && (
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-cs btn-contorno" onClick={onListo}>
              Cancelar
            </button>
            <button type="submit" className="btn-cs btn-primario" disabled={guardar.isPending}>
              Guardar
            </button>
          </div>
        )}
      </form>
      <section>
        <h3 className="mb-1 font-extrabold">Licencias</h3>
        <p className="mb-3 text-xs text-texto-3">
          Cada casilla se guarda al momento. {asignadas.size} licencia(s) con este elemento.
        </p>
        {resumen.data && (
          <ElegirLicencias
            licencias={resumen.data.memberships}
            elegidas={asignadas}
            deshabilitado={!editable || asignar.isPending}
            onCambio={(id, marcada) =>
              asignar.mutate({
                action: marcada ? 'add' : 'remove',
                libraryItemIds: [actual.id],
                membershipIds: [id],
              })
            }
          />
        )}
      </section>
    </div>
  );
}

/** Datos comunes de un elemento de la biblioteca y sus licencias. */
export function ElementoModal({ id, onCerrar }: { id: number | null; onCerrar: () => void }) {
  const q = useElemento(id);
  return (
    <Modal
      abierto={id !== null}
      onCerrar={onCerrar}
      titulo={q.data?.name ?? 'Elemento de la biblioteca'}
      ancho="max-w-2xl"
    >
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data && <Formulario key={q.data.id} actual={q.data} onListo={onCerrar} />}
    </Modal>
  );
}
